import { NavLink } from "react-router-dom";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import DirectionsBoatFilledRoundedIcon from "@mui/icons-material/DirectionsBoatFilledRounded";
import DescriptionIcon from "@mui/icons-material/Description";
import TaskIcon from "@mui/icons-material/Task";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowRightAltOutlinedIcon from "@mui/icons-material/ArrowRightAltOutlined";
import {
  Box,
  Avatar,
  CircularProgress,
  Paper,
  Tooltip,
  Dialog,
} from "@mui/material";
import { colorFromName } from "../../common/ColorFromName";
import {
  useGetJobOrderExecViewQuery,
  useGetJobOrderMetadataQuery,
  useGetVesselJobOrderAggregationsQuery,
} from "../../redux/api/jobOrder";
import { useGetFleetsQuery } from "../../redux/api/fleet";
import useDocumentTitle from "../../util/useDocumentTitle";
import { AccessTimeRounded } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { JobRatingLabel } from "../../common/JobOrder";
import NonUnionMariners from "./NonUnionMariners";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { UserGroup } from "../../common/User";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useGetVesselsForVesselExecQuery } from "../../redux/api/user";
import { useGetTrainingOrderMetadataQuery } from "../../redux/api/trainingOrder";
import { useGetComplianceTrainingReportQuery } from "../../redux/api/complianceTraining";
import VesselHeadCounts from "./VesselHeadCounts";
import TrainingReport from "../training/TrainingReport";
import ComplianceTrainingReport from "../training/ComplianceTrainingReport";
import GappedAndFilledBillets from './GappedAndFilledBillets';
import UpcomingExpiredOfficerDocuments from './UpcomingExpiredOfficerDocuments';

const Vessels = () => {
  useDocumentTitle("Vessels");
  const user = useSelector(selectCurrentUser);
  const hasTrainingAccess =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_MANAGERS ||
    (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES_ADMINS ||
    (user?.userGroup as UserGroup) === UserGroup.CREWING_SPECIALISTS ||
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EVAL_SPECIALISTS ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EXECS;
  const { data: fleets, isLoading: isFleetsLoading } = useGetFleetsQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: metadata, isLoading: isJobOrderMetadataLoading } =
    useGetJobOrderMetadataQuery(
      {},
      {
        refetchOnMountOrArgChange: true,
      }
    );
  const {
    data: trainingOrderMetadata,
  } = useGetTrainingOrderMetadataQuery(
    hasTrainingAccess ? { fleetId: undefined, vesselId: undefined } : skipToken,
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: complianceReport } = useGetComplianceTrainingReportQuery(
    undefined,
    { refetchOnMountOrArgChange: true, skip: !hasTrainingAccess }
  );
  const currDate = new Date();
  const [execViewParams, setExecViewParams] = useState<{
    startDate: string | undefined;
    endDate: string | undefined;
  }>({
    startDate: new Date(
      currDate.setDate(
        currDate.getDate() -
          currDate.getDay() +
          (currDate.getDay() === 0 ? -6 : 1)
      )
    ).toISOString(), // return the date of the Monday of this week, using Mon-Sun weeks
    endDate: new Date(
      currDate.setDate(
        currDate.getDate() -
          currDate.getDay() +
          (currDate.getDay() === 0 ? 0 : 7)
      )
    ).toISOString(), // return the date of the Sunday of this week, using Mon-Sun weeks
  });
  const { data: execView, isLoading: isJobOrderExecViewLoading } =
    useGetJobOrderExecViewQuery(execViewParams, {
      refetchOnMountOrArgChange: true,
    });
  const { data: aggregations, isLoading: isAggregateLoading } =
    useGetVesselJobOrderAggregationsQuery(
      {},
      {
        refetchOnMountOrArgChange: true,
      }
    );

  // store the vesselIDs that have been expanded
  const [expandedGappedBillets, setExpandedGappedBillets] = useState<number[]>(
    []
  );
  // keep track of whether the Non-Union Mariners Dialog is open
  const [nonUnionOpen, setNonUnionOpen] = useState(false);
  const [headCountsOpen, setHeadCountsOpen] = useState(false);
  const [trainingReportOpen, setTrainingReportOpen] = useState(false);
  const [complianceReportOpen, setComplianceReportOpen] = useState(false);
  const [gappedAndFilledOpen, setGappedAndFilledOpen] = useState(false);
  const [upcomingExpiredOpen, setUpcomingExpiredOpen] = useState(false);

  // collapse all when the fleets are updated
  useEffect(() => {
    collapseAll();
  }, [fleets]);

  const collapseAll = () => {
    setExpandedGappedBillets([]);
  };

  const expandAll = () => {
    if (!!fleets) {
      const temp = fleets.flatMap((f) => {
        return f.vessels
          .filter(
            (v) =>
              !!v.id && execView?.gappedBilletsByRating.some((b) => b.vesselId === v.id)
          )
          .map((v) => v.id);
      });
      // @ts-ignore ignore this type check since typescript cannot read that undefined is impossible from the filter() function
      setExpandedGappedBillets(temp);
    } else {
      setExpandedGappedBillets([]);
    }
  };

  // if Vessel Exec, will get allowed vessels, else returns empty list
  const { data: allowedVesselIds } = useGetVesselsForVesselExecQuery(
    user?.username
  );
  // calculate an array of fleets including the non-critical and critical rating counts of each Vessel
  const fleetsWithAggr =
    fleets &&
    aggregations &&
    allowedVesselIds &&
    fleets.map((fleet) => {
      if (allowedVesselIds.length === 0) {
        return {
          ...fleet,
          vessels: fleet.vessels.map((v) => {
            const currAggr = aggregations.find((a) => a.vesselId === v.id);
            return {
              ...v,
              criticalRatingList:
                currAggr && currAggr.criticalJobRatings.length > 0
                  ? currAggr.criticalJobRatings
                  : null,
              nonCriticalRatingList:
                currAggr && currAggr.nonCriticalJobRatings.length > 0
                  ? currAggr.nonCriticalJobRatings
                  : null,
              otherRatingList:
                currAggr && currAggr.otherJobRatings.length > 0
                  ? currAggr.otherJobRatings
                  : null,
            };
          }),
        };
      } else {
        return {
          ...fleet,
          vessels: fleet.vessels
            .filter((v) => allowedVesselIds.includes(v.id!))
            .map((v) => {
              const currAggr = aggregations.find((a) => a.vesselId === v.id);
              return {
                ...v,
                criticalRatingList:
                  currAggr && currAggr.criticalJobRatings.length > 0
                    ? currAggr.criticalJobRatings
                    : null,
                nonCriticalRatingList:
                  currAggr && currAggr.nonCriticalJobRatings.length > 0
                    ? currAggr.nonCriticalJobRatings
                    : null,
                otherRatingList:
                  currAggr && currAggr.otherJobRatings.length > 0
                    ? currAggr.otherJobRatings
                    : null,
              };
            }),
        };
      }
    });
  // TODO Filter all the fleets and only give select vessels VESSEL EXEC has access to
  // pre-calculate/filter Vessels with critical Job Orders
  const criticalFleets = fleetsWithAggr
    ?.map((fleet) => {
      return {
        ...fleet,
        vessels: fleet.vessels.filter((v) => !!v.criticalRatingList),
      };
    })
    ?.filter((fleet) => fleet.vessels.length > 0);
  // pre-calculate/filter Vessels with active, non-critical Job Orders
  const activeFleets = fleetsWithAggr
    ?.map((fleet) => {
      return {
        ...fleet,
        vessels: fleet.vessels.filter(
          (v) => !v.criticalRatingList && !!v.otherRatingList
        ),
      };
    })
    ?.filter((fleet) => fleet.vessels.length > 0);
  // pre-calculate/filter Vessels with no active Job Orders
  const noActionFleets = fleetsWithAggr
    ?.map((fleet) => {
      return {
        ...fleet,
        vessels: fleet.vessels.filter(
          (v) => !v.nonCriticalRatingList && !v.criticalRatingList
        ),
      };
    })
    ?.filter((fleet) => fleet.vessels.length > 0);

  if (
    isAggregateLoading ||
    isFleetsLoading ||
    isJobOrderMetadataLoading ||
    isJobOrderExecViewLoading
  ) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  const complianceVesselKeys = Array.from(new Set((complianceReport ?? []).map(r => `${r.fleetName}|${r.vesselName}`)));
  const compliantVesselCount = complianceVesselKeys.filter(key =>
    (complianceReport ?? []).filter(r => `${r.fleetName}|${r.vesselName}` === key).every(r => r.complete)
  ).length;
  const nonCompliantVesselCount = complianceVesselKeys.length - compliantVesselCount;

  return (
    <>
      <div className="flex-col wd-100">
        <div className="home-content mb-2">
          <div className="page-header align-items-center">
            <h1>Insights For</h1>
            <div className="flex g-1 align-items-center">
              <label htmlFor="execViewStartDate">
                <input
                  type="date"
                  id="execViewStartDate"
                  value={execViewParams.startDate?.substring(0, 10) || ""}
                  onChange={(event) =>
                    setExecViewParams({
                      ...execViewParams,
                      startDate: !!event.target.value
                        ? new Date(event.target.value).toISOString()
                        : undefined,
                    })
                  }
                />
              </label>
              <ArrowRightAltOutlinedIcon />
              <label htmlFor="execViewEndDate">
                <input
                  type="date"
                  id="execViewEndDate"
                  value={execViewParams.endDate?.substring(0, 10) || ""}
                  onChange={(event) =>
                    setExecViewParams({
                      ...execViewParams,
                      endDate: !!event.target.value
                        ? new Date(event.target.value).toISOString()
                        : undefined,
                    })
                  }
                />
              </label>
            </div>
          </div>
          <div className="rollup rollup-wrap">
            <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightblue">
              <div className="flex g-1">
                <DescriptionIcon className="color-blue" />
                <h2>{execView?.billetsToBeFilled ? execView.billetsToBeFilled : 0}</h2>
              </div>
              <p>Jobs Posted to be Filled</p>
            </div>
            <div className="statistic bg-lightgreen p-2 flex g-5 align-items-center">
              <Dialog
                maxWidth={false}
                open={gappedAndFilledOpen}
                onClose={() => setGappedAndFilledOpen(false)}
              >
                <GappedAndFilledBillets
                  gappedAndFilledBillets={
                    execView?.gappedAndFilledBillets ? execView.gappedAndFilledBillets : []
                  }
                  onClose={() => setGappedAndFilledOpen(false)}
                />
              </Dialog>
              <div className="flex-col align-items-start gr-0">
                <div className="flex g-1">
                  <TaskIcon className="color-green" />
                  <h2>{execView?.billetsFilled ? execView.billetsFilled : 0}</h2>
                </div>
                <p>Jobs Filled</p>
                <NavLink
                  to=""
                  className="blue-link"
                  onClick={() => setGappedAndFilledOpen(true)}
                >
                  View
                </NavLink>
              </div>
              <div className="flex-col align-items-start">
                <h4>Gapped Billets</h4>
                {fleets &&
                  execView?.gappedBilletsByRating &&
                  fleets.map((f) => {
                    return (
                      <div key={f.id} className="wd-140px">
                        <div className="vessel-heading flex g-1 justify-content-btwn wd-100">
                          <div className="flex g-1">
                            <div className="block ht-1px wd-1px bg-navy"></div>
                            <h4 className="ft-14">{f.name}</h4>
                          </div>
                          <div className="vessel-subheading wd-auto">
                            <h5 className="color-navy b p-6 m-6">
                              {execView.gappedBilletsByRating
                                .filter((b) =>
                                  f.vessels.some((v) => v.id === b.vesselId)
                                )
                                .reduce((acc, b) => acc + b.gappedBillets, 0)}
                            </h5>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
            <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightgrey">
              <div className="flex g-1">
                <PersonOutlineIcon className="color-grey" />
                <h2>
                  {execView?.nonUnionMariners
                    ? execView.nonUnionMariners.reduce(
                        (acc, cur) => acc + cur.nonUnionMariners,
                        0
                      )
                    : 0}
                </h2>
              </div>
              <Dialog
                open={nonUnionOpen}
                onClose={() => setNonUnionOpen(false)}
              >
                <NonUnionMariners
                  nonUnionMariners={
                    execView?.nonUnionMariners ? execView.nonUnionMariners : []
                  }
                  onClose={setNonUnionOpen}
                />
              </Dialog>
              <div className="flex-col align-items-start g-1">
                <p>Non-Union Mariners Currently Onboarded</p>
                <NavLink
                  to=""
                  className="blue-link"
                  onClick={() => setNonUnionOpen(true)}
                >
                  View
                </NavLink>
              </div>
            </div>
            <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightnavy">
              <div className="flex g-1">
                <PersonOutlineIcon className="color-grey" />
                <h2>
                  {execView?.vesselHeadCounts
                    ? execView.vesselHeadCounts.reduce(
                        (acc, cur) => acc + cur.headCount,
                        0
                      )
                    : 0}
                </h2>
              </div>
              <Dialog
                open={headCountsOpen}
                onClose={() => setHeadCountsOpen(false)}
                maxWidth="md"
              >
                <VesselHeadCounts
                  vesselHeadCounts={
                    execView?.vesselHeadCounts ? execView.vesselHeadCounts : []
                  }
                  onClose={setHeadCountsOpen}
                />
              </Dialog>
              <div className="flex-col align-items-start g-1">
                <p>Mariners Currently Onboard + ROS/FOS Status</p>
                <NavLink
                  to=""
                  className="blue-link"
                  onClick={() => setHeadCountsOpen(true)}
                >
                  View
                </NavLink>
              </div>
            </div>
            <div className="statistic p-2 flex-col gr-0 bg-lightred justify-content-center">
              <Dialog
                maxWidth={false}
                open={upcomingExpiredOpen}
                onClose={() => setUpcomingExpiredOpen(false)}
              >
                <UpcomingExpiredOfficerDocuments
                  upcomingExpiredOfficerDocuments={
                    execView?.upcomingExpiringPermanentOfficerDocuments ? execView.upcomingExpiringPermanentOfficerDocuments : []
                  }
                  onClose={() => setUpcomingExpiredOpen(false)}
                />
              </Dialog>
              <div className="flex g-1">
                <PersonOutlineIcon className="color-grey" />
                <h2>
                  {execView?.upcomingExpiringPermanentOfficerDocuments
                    ? execView.upcomingExpiringPermanentOfficerDocuments.reduce(
                        (acc, cur) => acc + 1,
                        0
                      )
                    : 0}
                </h2>
              </div>
                <div className="flex g-1">
                  <p>Upcoming Expiring Officer Documents</p>
                </div>
              <div className="flex-col align-items-start g-1">
                <NavLink
                  to=""
                  className="blue-link"
                  onClick={() => setUpcomingExpiredOpen(true)}
                >
                  View
                </NavLink>
              </div>
            </div>
            {trainingOrderMetadata && (
              <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightorange">
                <div className="flex g-1">
                  <h3>Mariner Training</h3>
                </div>
                <div className="vessel-heading flex g-1 justify-content-btwn wd-100">
                  <div className="flex g-1">
                    <div className="block ht-1px wd-1px bg-red"></div>
                    <h4>Expired</h4>
                  </div>
                  <div className="vessel-subheading wd-auto">
                    <h5 className="color-navy b p-6 m-6">
                      {trainingOrderMetadata?.expiredCount}
                    </h5>
                  </div>
                </div>
                <div className="vessel-heading flex g-1 justify-content-btwn wd-100">
                  <div className="flex g-1">
                    <div className="block ht-1px wd-1px bg-orange"></div>
                    <h4>Training Needed</h4>
                  </div>
                  <div className="vessel-subheading wd-auto">
                    <h5 className="color-navy b p-6 m-6">
                      {trainingOrderMetadata?.trainingNeededCount}
                    </h5>
                  </div>
                </div>
                <div className="vessel-heading flex g-1 justify-content-btwn wd-100">
                  <div className="flex g-1">
                    <div className="block ht-1px wd-1px bg-yellow"></div>
                    <h4>Pending Confirmation</h4>
                  </div>
                  <div className="vessel-subheading wd-auto">
                    <h5 className="color-navy b p-6 m-6">
                      {trainingOrderMetadata?.pendingConfirmationCount}
                    </h5>
                  </div>
                </div>
                <div className="vessel-heading flex g-1 justify-content-btwn wd-100">
                  <div className="flex g-1">
                    <div className="block ht-1px wd-1px bg-green"></div>
                    <h4>Completed</h4>
                  </div>
                  <div className="vessel-subheading wd-auto">
                    <h5 className="color-navy b p-6 m-6">
                      {trainingOrderMetadata?.completedCount}
                    </h5>
                  </div>
                </div>
                <div className="flex-col align-items-start g-1">
                  <div className="flex g-3 align-items-center view-links">
                    <NavLink to="/trainingInsights" className="blue-link">
                      View
                    </NavLink>
                    <NavLink
                      to=""
                      className="blue-link ml-2"
                      onClick={() => setTrainingReportOpen(true)}
                    >
                      View Training Report
                    </NavLink>
                  </div>
                </div>
                <Dialog
                  open={trainingReportOpen}
                  onClose={() => setTrainingReportOpen(false)}
                  maxWidth="lg"
                  fullWidth
                  className="training-report-dialog wide"
                >
                  <div className="training-report-container">
                    <TrainingReport
                      defaultStartDate={execViewParams.startDate}
                      defaultEndDate={execViewParams.endDate}
                      onClose={setTrainingReportOpen}
                    />
                  </div>
                </Dialog>
              </div>
            )}
            {hasTrainingAccess && (
              <div className="statistic p-2 flex-col gr-0 bg-lightpurple">
                <div className="flex g-1 mt-1 mb-2">
                  <h3>Compliance Training</h3>
                </div>
                <div className="vessel-heading flex g-1 justify-content-btwn wd-100">
                  <div className="flex g-1">
                    <div className="block ht-1px wd-1px bg-green"></div>
                    <h4>Compliant Vessels</h4>
                  </div>
                  <div className="vessel-subheading wd-auto">
                    <h5 className="color-navy b p-6 m-6">{compliantVesselCount}</h5>
                  </div>
                </div>
                <div className="vessel-heading flex g-1 justify-content-btwn wd-100 mb-2">
                  <div className="flex g-1">
                    <div className="block ht-1px wd-1px bg-red"></div>
                    <h4>Non-Compliant Vessels</h4>
                  </div>
                  <div className="vessel-subheading wd-auto">
                    <h5 className="color-navy b p-6 m-6">{nonCompliantVesselCount}</h5>
                  </div>
                </div>
                <div className="flex-col align-items-start g-1 mt-2">
                  <NavLink
                    to=""
                    className="blue-link"
                    onClick={() => setComplianceReportOpen(true)}
                  >
                    View Report
                  </NavLink>
                </div>
                <Dialog
                  open={complianceReportOpen}
                  onClose={() => setComplianceReportOpen(false)}
                  maxWidth="lg"
                  fullWidth
                  className="training-report-dialog wide"
                >
                  <div className="training-report-container">
                    <ComplianceTrainingReport rows={complianceReport} onClose={setComplianceReportOpen} />
                  </div>
                </Dialog>
              </div>
            )}
          </div>
        </div>

        <div className="flex align-items-center g-5 mb-1">
          <h2>Gapped Billets By Fleet</h2>
          <div className="flex g-3">
            <NavLink to="" className="blue-link" onClick={expandAll}>
              Expand All
            </NavLink>
            <NavLink to="" className="blue-link" onClick={collapseAll}>
              Collapse All
            </NavLink>
          </div>
        </div>

        <div className="gapped-billet flex g-2 mb-3">
          {fleets &&
            execView?.gappedBilletsByRating &&
            fleets.map((f) => {
              return (
                <Paper key={f.id} className="vessel-card ht-auto p-2">
                  <div>
                    <div className="vessel-heading flex justify-content-btwn mb-0">
                      <h3 className="ft-18">{f.name}</h3>
                      <div className="vessel-subheading wd-auto">
                        <h5 className="ft-20 color-navy b wd-auto mr-6">
                          {execView.gappedBilletsByRating
                            .filter((b) =>
                              f.vessels.some((v) => v.id === b.vesselId)
                            )
                            .reduce((acc, b) => acc + b.gappedBillets, 0)}
                        </h5>
                      </div>
                    </div>
                    <div className="underline bg-navy"></div>
                    <div className="flex-col g-1">
                      {f.vessels
                        .filter(
                          (v) =>
                            !!v.id &&
                            execView.gappedBilletsByRating.some(
                              (b) => b.vesselId === v.id
                            )
                        )
                        .map((v) => {
                          return (
                            <div key={v.id}>
                              <div className="flex-col">
                                <div className="flex align-items-center g-1">
                                  <h4
                                    className={
                                      !!v.id &&
                                      expandedGappedBillets.includes(v.id)
                                        ? "bg-navy block p-0 color-white ht-2px"
                                        : "bg-navy block color-white p-0 ht-2px"
                                    }
                                  >
                                    {execView.gappedBilletsByRating
                                      .filter((b) => b.vesselId === v.id)
                                      .reduce(
                                        (acc, b) => acc + b.gappedBillets,
                                        0
                                      )}
                                  </h4>
                                  <NavLink
                                    to=""
                                    className={
                                      !!v.id &&
                                      expandedGappedBillets.includes(v.id)
                                        ? "expanded b blue-link color-navy"
                                        : "blue-link color-navy"
                                    }
                                    onClick={() => {
                                      if (!!v.id) {
                                        const temp = [...expandedGappedBillets];
                                        const idx = temp.findIndex(
                                          (val) => val === v.id
                                        );
                                        if (idx < 0) {
                                          temp.push(v.id);
                                        } else {
                                          temp.splice(idx, 1);
                                        }
                                        setExpandedGappedBillets(temp);
                                      }
                                    }}
                                  >
                                    {v.name?.replace(`${f.name} `, "")}
                                  </NavLink>
                                </div>
                                {!!v.id &&
                                  expandedGappedBillets.includes(v.id) && (
                                    <ul>
                                      {execView.gappedBilletsByRating
                                        .filter((b) => b.vesselId === v.id)
                                        .map((b) => {
                                          return (
                                            <span
                                              className="flex align-items-center ml-4 g-1"
                                              key={`${v.id}-${b.rating}`}
                                            >
                                              <h4 className="bg-red block ft-14 wd-2px ht-15px color-white p-0">
                                                <>{b.gappedBillets}</>
                                              </h4>
                                              <h5 className="ft-14 b-600">
                                                {JobRatingLabel[b.rating]}
                                              </h5>
                                            </span>
                                          );
                                        })}
                                    </ul>
                                  )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </Paper>
              );
            })}
        </div>
        <div className="home-content mt-2">
          <div className="page-header">
            <h2>Vessels Overview</h2>
            <NavLink to="/jobOrders" className="blue-link">
              View All Job Orders
            </NavLink>
          </div>
          <div className="rollup">
            <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightorange">
              <div className="flex g-1">
                <RemoveCircleOutlineRoundedIcon className="color-orange" />
                <h2>
                  {metadata?.activeCount
                    ? metadata.activeCount - metadata.criticalCount
                    : 0}
                </h2>
              </div>
              <p>Non-Critical Job Orders Unfilled</p>
            </div>
            <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightblue">
              <div className="flex g-1">
                <AccessTimeRounded className="color-blue" />
                <h2>
                  {metadata?.monthCompletedCount
                    ? metadata.monthCompletedCount
                    : 0}
                </h2>
              </div>
              <p>Pending Job Orders</p>
            </div>
            <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightgreen">
              <div className="flex g-1">
                <CheckCircleOutlineRoundedIcon className="color-green" />
                <h2>
                  {metadata?.monthArchivedCount
                    ? metadata.monthArchivedCount
                    : 0}
                </h2>
              </div>
              <p>Job Orders Completed This Month</p>
            </div>
          </div>
        </div>

        {/* Vessels with open job orders  in the near future */}
        {criticalFleets && criticalFleets?.length > 0 && (
          <h2 className="vessel-group-heading bg-red wd-fit-content">
            Action Needed - Critical
          </h2>
        )}
        {criticalFleets &&
          criticalFleets.map((fleet) => {
            return (
              <div key={fleet.id}>
                <h3 className="fleet-header mt-1">
                  <DirectionsBoatFilledRoundedIcon />
                  {fleet.name}
                </h3>
                <div className="vessel-grid">
                  {fleet.vessels.map((v) => {
                    return (
                      <Paper key={v.id}>
                        <div className="vessel-card">
                          <div className="vessel-heading">
                            <h3>{v.name}</h3>
                            <WarningAmberRoundedIcon />
                          </div>
                          <div className="underline bg-red"></div>
                          {v.criticalRatingList?.map((r) => {
                            return (
                              <span
                                className="flex align-items-center ml-4 g-1"
                                key={`${r.jobRating}`}
                              >
                                <h4 className="bg-red block ft-14 wd-2px ht-15px color-white p-0">
                                  <>{r.ratingCount}</>
                                </h4>
                                <h5 className="ft-14 b-600">
                                  {JobRatingLabel[r.jobRating]}
                                </h5>
                              </span>
                            );
                          })}
                          {!!v.nonCriticalRatingList &&
                            v.nonCriticalRatingList.map((r) => {
                              return (
                                <span
                                  className="flex align-items-center ml-4 g-1"
                                  key={`${r.jobRating}`}
                                >
                                  <h4 className="bg-orange block ft-14 wd-2px ht-15px color-white p-0">
                                    {r.ratingCount}
                                  </h4>
                                  <h5 className="ft-14 b-600">
                                    {JobRatingLabel[r.jobRating]}
                                  </h5>
                                </span>
                              );
                            })}
                          <span className="flex align-items-center ml-4 g-1">
                            <NavLink
                              to={`/jobOrders?vesselId=${v.id}`}
                              className="blue-link"
                            >
                              View
                            </NavLink>
                            <div className="spacer"></div>
                            <Tooltip
                              title={
                                v?.defaultAssignee?.name ||
                                v?.defaultAssignee?.email ||
                                ""
                              }
                              placement="right"
                            >
                              <Avatar
                                className="flex align-self-end mt-1"
                                sx={{
                                  bgcolor: colorFromName(
                                    `${v?.defaultAssignee?.name}`
                                  ),
                                  height: 30,
                                  width: 30,
                                }}
                              >
                                {v?.defaultAssignee
                                  ? v?.defaultAssignee.name?.charAt(0)
                                  : undefined}
                              </Avatar>
                            </Tooltip>
                          </span>
                        </div>
                      </Paper>
                    );
                  })}
                </div>
              </div>
            );
          })}

        {/* Vessels with open job orders */}
        {activeFleets && activeFleets?.length > 0 && (
          <h2 className="vessel-group-heading bg-orange wd-fit-content">
            Action Needed
          </h2>
        )}
        {activeFleets &&
          activeFleets.map((fleet) => {
            return (
              <div key={fleet.id}>
                <h3 className="fleet-header mt-1">
                  <DirectionsBoatFilledRoundedIcon />
                  {fleet.name}
                </h3>
                <div className="vessel-grid">
                  {fleet.vessels.map((v) => {
                    return (
                      <Paper key={v.id}>
                        <div className="vessel-card">
                          <h3>{v.name}</h3>
                          <div className="underline bg-orange"></div>
                          {!!v.nonCriticalRatingList &&
                            v.nonCriticalRatingList.map((r) => {
                              return (
                                <span
                                  className="flex align-items-center ml-4 g-1"
                                  key={`${r.jobRating}`}
                                >
                                  <h4 className="bg-orange block ft-14 wd-2px ht-15px color-white p-0">
                                    {r.ratingCount}
                                  </h4>
                                  <h5 className="ft-14 b-600">
                                    {JobRatingLabel[r.jobRating]}
                                  </h5>
                                </span>
                              );
                            })}
                          <span className="flex align-items-center ml-4 g-1">
                            <NavLink
                              to={`/jobOrders?vesselId=${v.id}`}
                              className="blue-link"
                            >
                              View
                            </NavLink>
                            <div className="spacer"></div>
                            <Tooltip
                              title={
                                v?.defaultAssignee?.name ||
                                v?.defaultAssignee?.email ||
                                ""
                              }
                              placement="right"
                            >
                              <Avatar
                                className="flex align-self-end mt-1"
                                sx={{
                                  bgcolor: colorFromName(
                                    `${v?.defaultAssignee?.name}`
                                  ),
                                  height: 30,
                                  width: 30,
                                }}
                              >
                                {v?.defaultAssignee
                                  ? v?.defaultAssignee.name?.charAt(0)
                                  : undefined}
                              </Avatar>
                            </Tooltip>
                          </span>
                        </div>
                      </Paper>
                    );
                  })}
                </div>
              </div>
            );
          })}

        {/* Fully staffed vessels */}
        {noActionFleets && noActionFleets?.length > 0 && (
          <h2 className="vessel-group-heading bg-green wd-fit-content">
            No Action Needed
          </h2>
        )}
        {noActionFleets &&
          noActionFleets.map((fleet) => {
            return (
              <div key={fleet.id}>
                <h3 className="fleet-header mt-1">
                  <DirectionsBoatFilledRoundedIcon />
                  {fleet.name}
                </h3>
                <div className="vessel-grid">
                  {fleet.vessels.map((v) => {
                    return (
                      <Paper key={v.id}>
                        <div className="vessel-card">
                          <h3>{v.name}</h3>
                          <div className="underline bg-green"></div>
                          <div className="vessel-subheading">
                            <p>No Open Job Orders</p>
                          </div>
                          <div className="spacer"></div>
                          <Tooltip
                            title={
                              v?.defaultAssignee?.name ||
                              v?.defaultAssignee?.email ||
                              ""
                            }
                            placement="right"
                          >
                            <Avatar
                              className="flex align-self-end mt-1"
                              sx={{
                                bgcolor: colorFromName(
                                  v?.defaultAssignee?.name || ""
                                ),
                                height: 30,
                                width: 30,
                              }}
                            >
                              {v?.defaultAssignee
                                ? v.defaultAssignee.name?.charAt(0)
                                : undefined}
                            </Avatar>
                          </Tooltip>
                        </div>
                      </Paper>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};

export default Vessels;
